import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Tabulator } from 'react-tabulator/lib/types/TabulatorTypes';
import { useLanguageManagement } from '@/domains/system-management/hooks/useLanguageManagement';
import type { ButtonState, Language, Params } from '@/domains/system-management/types/language';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import {
  type ColumnDefinition,
  ReactTabulator,
} from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

const commonColumnSettings = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false,
};

const LanguageManagement = () => {
  const { t } = useTranslation();

  const tbRef = useRef<Tabulator | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const tagRef = useRef<HTMLInputElement | null>(null);
  const engRef = useRef<HTMLInputElement | null>(null);

  const hasChangesUpdateRef = useRef<boolean>(false);
  const hasChangesCreateRef = useRef<boolean>(false);
  const isNewClickedRef = useRef<boolean>(false);
  const selectedLanguageRef = useRef<{ tag?: string | null }>({ tag: null });

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [disabledTag, setDisabledTag] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [newTag, setNewTag] = useState<string | null>(null);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<{
    tag?: string | null;
  }>({ tag: null });

  const [formValues, setFormValues] = useState<Partial<Language>>({});

  const emptyDetail = () => {
    setFormValues(
      (prev) => Object.fromEntries(Object.keys(prev).map((key) => [key, ''])) as Partial<Language>
    );
  };

  // Button states
  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = () =>
    setButtonState({ confirm: true, cancel: true, delete: true, create: true });

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prev) => ({ ...prev, create: false }));
  };
  const enableUpdateButtons = () => {
    disableAllButtons();
    setButtonState((prev) => ({
      ...prev,
      cancel: false,
      delete: false,
      create: false,
    }));
  };
  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false }));
  };

  // CRUD callbacks
  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setDisabledTag(true);
    setHasChangesUpdate(false);
    setSelectedLanguage({ tag: null });
  };

  const createCallback = () => {
    tbRef.current?.deselectRow();
    enableInitialButtons();
    emptyDetail();
    setDisabledForm(true);
    setDisabledTag(true);
    setHasChangesCreate(false);
    setSelectedLanguage({ tag: null });
    setNewTag(null);
  };

  const updateCallback = () => {
    enableUpdateButtons();
    setHasChangesUpdate(false);
  };

  // API hooks
  const [queryParams, setQueryParams] = useState<Params>({
    deletion: '001002',
  });

  const [optionParams] = useState<string>('upper-code=001');

  const { useLanguages, useLanguage, useCreateLanguage, useUpdateLanguage, useDeleteLanguage } =
    useLanguageManagement(queryParams);

  const { data: languagesData } = useLanguages();
  const { data: languageData, error: languageError } = useLanguage(selectedLanguage?.tag ?? '');
  const createLanguageMutation = useCreateLanguage((responseData?: { tag: string }) => {
    createCallback();
    const createdTag = responseData?.tag ?? null;
    setNewTag(createdTag);
  });
  const updateLanguageMutation = useUpdateLanguage(() => {
    updateCallback();
  });
  const deleteLanguageMutation = useDeleteLanguage(() => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setDisabledTag(true);
    setHasChangesUpdate(false);
    setSelectedLanguage({ tag: null });
    setNewTag(null);
  });

  const { commonListData } = useCommonCodes({ optionParams });

  const optionsRadioFilter = commonListData?.['001']
    ? [
        { value: 'All', label: t('cmn > all'), code: 'All' },
        ...(commonListData['001']?.code?.map((code, index) => ({
          value: code ?? '',
          label: commonListData['001']?.name[index] ?? '',
          code: code ?? '',
        })) || []),
      ]
    : [];

  const columns = [
    {
      title: t('cmn > no'),
      formatter: (cell: Tabulator.CellComponent) => {
        const row = cell.getRow();
        const page = row.getTable().getPage();
        const pageSize = row.getTable().getPageSize();
        return (Number(page) - 1) * Number(pageSize) + row.getPosition(true);
      },
      width: 65,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('language > tag'),
      field: 'tag',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('language > indonesia'),
      field: 'ind',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('language > english'),
      field: 'eng',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnSettings,
    },
  ];

  // Tabulator options
  const languageTabulator = () => ({
    pagination: {
      first: t('cmn > first page'),
      first_title: t('cmn > first page'),
      last: t('cmn > last page'),
      last_title: t('cmn > last page'),
      prev: t('cmn > page before'),
      prev_title: t('cmn > page before'),
      next: t('cmn > next page'),
      next_title: t('cmn > next page'),
    },
  });

  const optionsTabulator = {
    debugInvalidOptions: true,
    pagination: true,
    movableRows: false,
    resizableRows: false,
    index: 'tag',
    locale: 'ko',
    langs: { ko: languageTabulator() },
    paginationSize: 10,
    selectableRows: false,
    rowHeight: 41,
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${t(
      'cmn > total'
    )} ${languagesData?.length || 0} ${t('cmn > results')}</div>`,
    selectableRowsCheck: (row: Tabulator.RowComponent) =>
      !row.getElement().classList.contains('tabulator-selected'),
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
  };

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, []);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    isNewClickedRef.current = isNewClicked;
    selectedLanguageRef.current = selectedLanguage;
  }, [hasChangesUpdate, hasChangesCreate, isNewClicked, selectedLanguage]);

  useEffect(() => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    if (languageError) new NoticeMessage(t('msg > load data fail'));
  }, [languageError, t]);

  useEffect(() => {
    if (!languageData) return;
    setFormValues({
      tag: languageData.tag,
      eng: languageData.eng,
      ind: languageData.ind,
    });
  }, [languageData]);

  // Handlers
  const handleRowSelected = (row: Tabulator.RowComponent) => {
    const rowData: Language = row.getData();

    if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message
        .confirmClicked()
        .then(() => {
          tbRef.current?.deselectRow();
          row.select();
          row.getElement().classList.add('custom-hover');

          setSelectedLanguage({ tag: rowData.tag });
          setDisabledForm(false);
          setDisabledTag(true);
          setHasChangesUpdate(false);
          setHasChangesCreate(false);
          setIsNewClicked(false);

          requestAnimationFrame(() => {
            engRef.current?.focus();
          });
        })
        .catch(() => {
          row.deselect();
          const prevTag = selectedLanguageRef.current?.tag;
          if (prevTag) {
            const prevRow = tbRef.current?.getRow(prevTag) || null;
            prevRow?.select();
            prevRow?.getElement().classList.add('custom-hover');
          }
        });
    } else {
      tbRef.current?.deselectRow();
      row.select();
      row.getElement().classList.add('custom-hover');

      setSelectedLanguage({ tag: rowData.tag });
      setDisabledForm(false);
      setDisabledTag(true);
      enableUpdateButtons();
      setIsNewClicked(false);

      requestAnimationFrame(() => {
        engRef.current?.focus();
      });
    }
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      setFormValues((prev) => ({ ...prev, [name]: value }));

      if (isNewClickedRef.current) setHasChangesCreate(true);
      else setHasChangesUpdate(true);

      if (selectedLanguageRef.current?.tag) setHasChangesUpdate(true);
    },
    []
  );

  const handleSearch = useCallback(
    (inputVal: string | null = null, radioVal: string | null = null) => {
      const params: Params = {};

      if (inputVal) {
        params.input = inputVal;
      }

      if (radioVal && radioVal !== 'All') {
        params.deletion = radioVal;
      } else {
        params.deletion = '001002';
      }

      setQueryParams(params);
    },
    []
  );

  const handleNewButtonClick = () => {
    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(true);
        setDisabledTag(false);
        enableRegisterButtons();
        setSelectedLanguage({ tag: null });
        setDisabledForm(false);
        setHasChangesUpdate(false);
        requestAnimationFrame(() => tagRef.current?.focus());
      });
    } else {
      tbRef.current?.deselectRow();
      emptyDetail();
      setIsNewClicked(true);
      setDisabledTag(false);
      enableRegisterButtons();
      setSelectedLanguage({ tag: null });
      setDisabledForm(false);
      setHasChangesUpdate(false);
      requestAnimationFrame(() => tagRef.current?.focus());
    }
  };

  const handleRegistButtonClick = () => {
    const isEmptyField = !formValues.tag || !formValues.eng || !formValues.ind;

    if (isEmptyField) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('errmsg > required'));
      return;
    }

    createLanguageMutation.mutateAsync(formValues);
  };

  const handleConfirmButtonClick = () => {
    const isEmptyField = !formValues.eng || !formValues.ind;

    if (isEmptyField) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('errmsg > required'));
      return;
    }

    updateLanguageMutation.mutateAsync({
      tag: selectedLanguage?.tag ?? '',
      data: formValues,
    });
  };

  const handleCancelButtonClick = () => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        setDisabledTag(true);
        enableInitialButtons();
        setHasChangesUpdate(false);
      });
      return;
    }

    if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          setDisabledTag(true);
          enableInitialButtons();
          setHasChangesCreate(false);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        setDisabledTag(true);
        enableInitialButtons();
        setHasChangesCreate(false);
      }
      return;
    }

    reloadCallback();
  };

  const handleDeleteButtonClick = () => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      deleteLanguageMutation.mutateAsync(selectedLanguage?.tag ?? '');
    });
  };

  const logs = languageData
    ? [
        {
          label: t('cmn > registered by'),
          value: languageData.registered_by ?? '',
        },
        {
          label: t('cmn > registered time'),
          value: languageData.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(languageData.registered_time))
            : '',
        },
        { label: t('cmn > updated by'), value: languageData.updated_by ?? '' },
        {
          label: t('cmn > updated time'),
          value: languageData.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(languageData.updated_time))
            : '',
        },
      ]
    : [];

  return (
    <>
      <section className="wrap">
        <div className="header-title">
          <h3>{t('SYSTEM')}</h3>
          <h3>&gt;</h3>
          <h3>{t('SYSTEM-LANGUAGE')}</h3>
        </div>

        <ContainerCard>
          <Filtering
            searchRef={searchRef}
            placeholder={t('language > tag')}
            onSearch={handleSearch}
            optionsRadioFilter={optionsRadioFilter}
          />
        </ContainerCard>
      </section>

      <ContainerCard>
        <ReactTabulator
          id="language-management-table"
          className="container-tabullator"
          data={languagesData || []}
          columns={columns as unknown as ColumnDefinition[]}
          layout={'fitDataStretch'}
          options={optionsTabulator}
          onRef={(r: { current: Tabulator | null }) => {
            tbRef.current = r.current;
          }}
          events={{
            rowClick: (_e: UIEvent, row: Tabulator.RowComponent) => handleRowSelected(row),
            tableBuilt: () => {
              if (selectedLanguage?.tag) {
                const row = tbRef.current?.getRow(selectedLanguage.tag) || null;
                row?.select();
                row?.getElement().classList.add('custom-hover');
              } else if (newTag) {
                const row = tbRef.current?.getRow(newTag) || null;
                if (row) {
                  tbRef.current?.scrollToRow(row, 'bottom', true);
                  row.select();
                  row.getElement().classList.add('custom-hover');
                  tbRef.current?.selectRow([newTag]);
                  handleRowSelected(row);
                }
              }
            },
          }}
        />
      </ContainerCard>

      <ContainerCard>
        <div className="flex w-full flex-col gap-[10px]">
          <div className="flex flex-col gap-4">
            <DetailForm
              label={t('language > tag')}
              value={formValues.tag || ''}
              inputType={'text'}
              onChange={handleInputChange}
              name={'tag'}
              disabled={disabledTag}
              required
              formRef={tagRef}
            />
            <DetailForm
              label={t('language > english')}
              value={formValues.eng || ''}
              inputType={'text'}
              onChange={handleInputChange}
              name={'eng'}
              disabled={disabledForm}
              required
              formRef={engRef}
            />
            <DetailForm
              label={t('language > indonesia')}
              value={formValues.ind || ''}
              inputType={'text'}
              onChange={handleInputChange}
              name={'ind'}
              disabled={disabledForm}
              required
            />
          </div>

          <hr className="border-t border-gray-300" />

          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">{disabledForm ? null : <LogList logs={logs} />}</div>
            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </>
  );
};

export default LanguageManagement;
